import {
    IPsoMarketConfiguration,
    IPsoMarketSeasonConfiguration, PsoSeasonIntendedForEnum
} from "../configuration/objects/pso-markets-configuration.interface";
import {IPsoMarket, IPsoMarketSeason, IPsoMarketTranslations} from "./pso-market.interface";
import {IServiceFactory} from "../service-factory.interface";
import { NullableString } from "../../types/nullable-types";
import {UserSessionClassesOfService} from "../dot-rez-api/session/user-session/user-session-classes-of-service";
import {PsoUserOptionsEnum} from "../flight-search/flight-search-controller/pso.enums";



export class PsoMarket implements IPsoMarket {
    constructor(private readonly configuration: IPsoMarketConfiguration,
                private readonly services: IServiceFactory) {
        this._seasons = (configuration.seasons ?? []).map(seasonConfig => new PsoMarketSeason(seasonConfig, services));
        this.translations = new PsoMarketTranslations(configuration, services);
    }

    private readonly _seasons: PsoMarketSeason[];
    readonly translations: PsoMarketTranslations;

    get id(): number {
        return this.configuration.id;
    }

    get marketName(): string {
        return this.configuration.marketName;
    }

    get bookDateStart(): Date {
        return this.services.time.parseIsoDate(this.configuration.bookDateStart);
    }

    get bookDateUntil(): Date {
        return this.services.time.parseIsoDate(this.configuration.bookDateUntil);
    }

    isApplicableAtBookingDate(bookingDate: Date): boolean {
        return this.bookDateStart.getTime() <= bookingDate.getTime() && bookingDate.getTime() <= this.bookDateUntil.getTime();
    }

    get departureDateStart(): Date {
        return this.services.time.parseIsoDate(this.configuration.departureDateStart);
    }

    get departureDateUntil(): Date {
        return this.services.time.parseIsoDate(this.configuration.departureDateUntil);
    }

    isApplicableAtDepartureDate(departureDate: Date): boolean {
        return this.departureDateStart.getTime() <= departureDate.getTime() && departureDate.getTime() <= this.departureDateUntil.getTime();
    }

    getAgentForUserOption(posUserOption: PsoUserOptionsEnum): NullableString {
        switch (posUserOption) {
            case PsoUserOptionsEnum.Resident:
                return this.configuration.agent;
            default:
                return this.getAgentForSeason(posUserOption)

        }
    }


    getAgentForSeason(posUserOption: PsoUserOptionsEnum): NullableString {
        for(const season of this._seasons) {
            if(season.getPsoUserOption() === posUserOption) {
                return season.agent;
            }
        }
        return null;
    }

    get allowNewBookings(): boolean {
        return this.configuration.allowNewBookings;
    }

    get isUsingClassesOfService(): boolean {
        return this.configuration.useClassesOfService;
    }

    canBeAppliedForUserClassesOfService(userClassesOfService: UserSessionClassesOfService): boolean {
        if(this.isUsingClassesOfService) {
            return userClassesOfService.pso(this.allPsoClassesOfService).length > 0
        }

        return true;
    }

    getClassesOfServiceForSearch(userClassesOfService: UserSessionClassesOfService, psoUserOption: PsoUserOptionsEnum): string[] {


        if(psoUserOption === PsoUserOptionsEnum.Resident) {
            return userClassesOfService.pso(this.classesOfServiceForResident);
        }

        for(const season of this._seasons) {
            if(season.getPsoUserOption() === psoUserOption) {
                const seasonClasses = this.getClassesOfServiceForSeason(season);
                return userClassesOfService.pso(seasonClasses);
            }

        }

        return userClassesOfService.regular(this.allPsoClassesOfService);
    }

    get classesOfServiceForResident(): string[] {
        if(this.isUsingClassesOfService) {
            return this.services.configuration.data.psoMarketsV2.classesOfService.forResident ?? [];
        }
        return [];
    }


    getClassesOfServiceForSeason(season: PsoMarketSeason): string[] {
        if(this.isUsingClassesOfService) {
            return season.getClassesOfService();
        }

        return [];
    }


    get allPsoClassesOfService(): string[] {
        return [
            ...this.classesOfServiceForResident,
            ...this._seasons.selectMany(s => this.getClassesOfServiceForSeason(s))
        ].distinct(c => c);
    }


    getActiveSeasons(atDate: Date): PsoMarketSeason[] {
        return this._seasons.filter(s => s.enabled && s.startDate.getTime() <= atDate.getTime() && atDate.getTime() <= s.endDate.getTime());
    }
}

class PsoMarketSeason implements IPsoMarketSeason {
    constructor(private readonly seasonConfig: IPsoMarketSeasonConfiguration, private readonly services: IServiceFactory) {
    }

    get name(): string {
        return this.seasonConfig.name;
    }


    get intendedFor(): PsoSeasonIntendedForEnum {
        return this.seasonConfig.intendedFor;
    }

    getPsoUserOption(): PsoUserOptionsEnum {
        switch (this.intendedFor) {
            case PsoSeasonIntendedForEnum.Families:
                return PsoUserOptionsEnum.Family;
            default:
                return PsoUserOptionsEnum.Worker;
        }
    }

    get enabled(): boolean {
        return this.seasonConfig.enabled;
    }


    get agent(): string {
        return this.seasonConfig.agent;
    }

    get startDate(): Date {
        if(this.seasonConfig.startDate) {
            return this.services.time.parseIsoDate(this.seasonConfig.startDate);
        } else {
            return new Date(1970, 1, 1);
        }
    }

    get endDate(): Date {
        if(this.seasonConfig.endDate) {
            return this.services.time.parseIsoDate(this.seasonConfig.endDate);
        } else {
            return new Date(9999, 12, 31);
        }
    }

    getClassesOfService(): string[] {
        switch (this.intendedFor) {
            case PsoSeasonIntendedForEnum.Families:
                return this.services.configuration.data.psoMarketsV2.classesOfService?.forFamily ?? [];
            default:
                return this.services.configuration.data.psoMarketsV2.classesOfService?.forWorker ?? [];
        }

    }
}

class PsoMarketTranslations implements IPsoMarketTranslations {
    constructor(private readonly configuration: IPsoMarketConfiguration,
                private readonly services: IServiceFactory) {
    }

    private get isSicily(): boolean {
        return this.configuration.marketName.toLowerCase() === 'sicilia';
    }

    getMarketNameTranslation(): string {
        switch (this.configuration.marketName.toLowerCase()) {
            case 'sardinia':
                return this.services.language.translate('Sardinia');
            case 'marche':
                return this.services.language.translate('Marche');
            case 'sicilia':
                return this.services.language.translate('Sicily');
            default:
                const marketName = this.configuration.marketName;
                return marketName[0].toUpperCase() + marketName.substring(1);
        }
    }

    get iAmResidentInMarket(): string {
        if(this.isSicily) {
            return this.services.language.translate('I am resident in Sicily')
        } else {
            return this.services.language.translationFor('I am a resident in {market}').withParams({market: this.getMarketNameTranslation()});
        }
    }

    get iWorkInMarket(): string {
        if(this.isSicily) {
            return this.services.language.translate('I work in Sicily');
        } else {
            return this.services.language.translationFor('I work in {market}').withParams({market: this.getMarketNameTranslation()});
        }
    }

    get weAreAFamilyFromMarket(): string {
        switch (this.configuration.marketName.toLowerCase()) {
            case 'sardinia':
                return this.services.language.translate('I am a relative/legal guardian of a resident in Sardinia');
            case 'sicilia':
                return this.services.language.translate('I am a relative/legal guardian of a resident in Sicily');
            default:
                return "I am a parental";
        }
    }

    getPsoUserOptionTranslation(psoUserOption: PsoUserOptionsEnum): string {
        switch (psoUserOption) {
            case PsoUserOptionsEnum.Resident:
                return this.iAmResidentInMarket;
            case PsoUserOptionsEnum.Worker:
                return this.iWorkInMarket;
            case PsoUserOptionsEnum.Family:
                return this.weAreAFamilyFromMarket;
            default:
                return this.iAmResidentInMarket;
        }
    }

}
